import { getQueryTransformation } from '@/api/helpers';
import { FiltersType, FilterValue } from '@/api/types';
import { Campaign, CampaignUserResult } from '@/client/campaigns';
import { MasterTemplateUserResult } from '@/client/campaigns/types/master-template-report.type';
import {
  FilterNamesEnum,
  getFilters,
  getFiltersFromColumns,
} from '@/client/helpers';
import { UsersReportType } from '@/client/reports';
import { Actions, Subjects } from '@/client/users';
import { TableNamesEnum } from '@/common/constants';
import { CampaignUserEventsTimeline } from '@/components/campaigns/CampaignUserEventsTimeline';
import { ReportsExportModal } from '@/components/reports/ReportsExportModal';
import {
  DataTable,
  DataTableAPIColumnsMultiSelect,
  DataTableColumnType,
  DataTableFilters,
  DataTableFiltersHandle,
  DataTableToolbar,
  FilterTypeEnum,
} from '@/components/tables/crud';
import { useCampaignUserResults } from '@/hooks/query';
import { useQueryParams } from '@/hooks/query.hook';
import { useMasterTemplateUserResults } from '@/hooks/query/master-template-reports.hooks';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DateFormats } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import {
  branchAdminCheck,
  getCampaignEventStatuses,
  getCampaignUserResultStatuses,
  getPhishingLanguageLabel,
  getYesFirstSort,
  hasFalsePositive,
} from '@/utils/helpers';
import { getBranchName } from '@/utils/helpers/branches.helper';
import {
  getCampaignReportsStatusSeverity,
  getRepotedStatus,
} from '@/utils/helpers/ui.helper';
import { uniqueId } from 'lodash';
import {
  DataTablePageEvent,
  DataTableRowClickEvent,
} from 'primereact/datatable';
import { Tooltip } from 'primereact/tooltip';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export type CampaignReportsDatatableSource =
  | { type: 'campaign' }
  | { type: 'master-template'; accountId: string; masterTemplateId: string };

type CampaignReportsDatatableProps = {
  shouldRefetch?: boolean;
  onShouldRefetchChange: () => void;
  campaign?: Campaign;
  source?: CampaignReportsDatatableSource;
  hideExport?: boolean;
  showRunColumns?: boolean;
  defaultFilters?: FiltersType;
  defaultTableFilters?: Record<string, any>;
  withToolbar?: boolean;
  toolbarAdditional?: ReactNode;
  onFiltersChange?: (filters: FiltersType) => void;
  filtersRef?: React.RefObject<DataTableFiltersHandle>;
  onRawFiltersChange?: (rawValues: Record<string, any>) => void;
};

export const CampaignReportsDatatable: React.FC<
  CampaignReportsDatatableProps
> = ({
  shouldRefetch,
  onShouldRefetchChange,
  campaign,
  defaultFilters = [],
  defaultTableFilters = {},
  withToolbar = false,
  toolbarAdditional,
  onFiltersChange,
  filtersRef,
  onRawFiltersChange,
  source = { type: 'campaign' },
  hideExport = false,
  showRunColumns = false,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { canUseFeature } = useFeatureFlag();
  const { can } = usePermission();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);

  const isBranchAdmin = branchAdminCheck(currentUser, currentAccount);

  const [expandedRows, setExpandedRows] = useState(undefined);
  const [showExportModal, setShowExportModal] = useState(false);

  const [skip, setSkip] = useState<number>(0);
  const [take, setTake] = useState<number>(10);

  const onPage = (event: DataTablePageEvent) => {
    setSkip(event.first);
    setTake(event.rows);
  };

  const { params, setSearchParams } = useQueryParams(
    getQueryTransformation<{ name: string; email: string }>(),
  );
  const [apiFilters, setFilters] = useState<FiltersType>(
    params.filters ? getFilters(params.filters) : [],
  );
  const [filterValues, setFilterValues] = useState<Record<string, any>>(
    params.filters ? params.filters : {},
  );
  const [sort, setSort] = useState<[string, 'asc' | 'desc'] | null>(
    params['sort[]'] ? params['sort[]'] : ['created', 'desc'],
  );

  const isMasterTemplateSource = source.type === 'master-template';
  const resultsFilters = [...apiFilters, ...defaultFilters];
  const resultsSort = sort && sort.length > 0 ? [sort.join(',')] : [];

  const campaignResultsQuery = useCampaignUserResults(
    {
      take,
      skip,
      filters: resultsFilters,
      sort: resultsSort,
      ...(isMasterTemplateSource ? { enabled: false } : {}),
    },
    id,
  );

  const masterTemplateResultsQuery = useMasterTemplateUserResults(
    isMasterTemplateSource ? source.accountId : undefined,
    isMasterTemplateSource ? source.masterTemplateId : undefined,
    {
      take,
      skip,
      filters: resultsFilters,
      sort: resultsSort,
      enabled: isMasterTemplateSource,
    },
  );

  const { isLoading, refetch, isFetching } = isMasterTemplateSource
    ? masterTemplateResultsQuery
    : campaignResultsQuery;
  const userResults = isMasterTemplateSource
    ? masterTemplateResultsQuery.masterTemplateUserResults
    : campaignResultsQuery.campaignUserResults;

  useEffect(() => {
    if (!shouldRefetch) return;

    const doRefetch = async () => {
      await refetch();

      onShouldRefetchChange();
    };

    doRefetch();
  }, [shouldRefetch]);

  const onSort = (event: DataTablePageEvent) => {
    setSkip(0);
    setSort(getYesFirstSort(event.sortField, event.sortOrder, sort));
  };

  useEffect(() => {
    setSearchParams({
      filters: filterValues,
      'sort[]': sort,
    });
  }, [JSON.stringify(filterValues), JSON.stringify(sort)]);

  const columns: DataTableColumnType[] = [
    {
      field: 'firstName',
      header: t('generic.name'),
      exportName: 'name',
      sortable: true,
      filterable: true,
      frozen: true,
      filters: {
        field: FilterNamesEnum.NAME,
        type: FilterTypeEnum.TEXT,
        placeholder: t('generic.name.search'),
      },
      render: (row: CampaignUserResult) =>
        row.firstName ? row.firstName : row.email,
    },
    {
      field: 'lastName',
      header: t('generic.lastName'),
      sortable: true,
      filterable: false,
      render: (row: CampaignUserResult) =>
        row.lastName ? row.lastName : <div>&#8212;</div>,
    },
    {
      field: 'email',
      header: t('generic.email'),
      sortable: true,
      filterable: true,
      filters: {
        type: FilterTypeEnum.TEXT,
        placeholder: t('generic.email.search'),
      },
      render: (row: CampaignUserResult) => row.email,
    },
    {
      field: 'user.username',
      exportName: 'username',
      header: t('generic.username'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_BY_USERNAME_NESTED,
        type: FilterTypeEnum.TEXT,
        placeholder: t('user.search.username'),
      },
      render: (row: CampaignUserResult) =>
        row.user?.username ? row.user.username : <span>&#8212;</span>,
    },
    {
      field: 'user.branch',
      exportName: 'branch',
      header: t('branch'),
      sortable: true,
      filterable: true,
      filters: {
        placeholder: t('filter.branch'),
        field: FilterNamesEnum.USER_BRANCH,
        type: FilterTypeEnum.MULTI_SELECT,
      },
      render: (row: CampaignUserResult) => {
        const id = uniqueId('branch');
        return (
          <>
            {row?.user?.branch ? (
              <>
                {row?.user?.branch && (
                  <Tooltip
                    position="mouse"
                    target={`.${id}`}
                    content={getBranchName(row.user.branch)
                      .reverse()
                      .join(' / ')}
                  />
                )}
                <div className={id}>{row?.user?.branch?.name}</div>
              </>
            ) : (
              <div className="group-row">&#8212;</div>
            )}
          </>
        );
      },
    },
    ...(!isBranchAdmin
      ? [
          {
            field: 'user.groups',
            exportName: 'groups',
            header: t('group'),
            sortable: true,
            filterable: true,
            filters: {
              placeholder: t('filter.group'),
              field: FilterNamesEnum.USER_GROUPS,
              type: FilterTypeEnum.MULTI_SELECT,
            },
            className: 'no-padding',
            render: (row: CampaignUserResult) => {
              return (
                <>
                  {row?.user?.groups.length ? (
                    row.user.groups.map((group) => (
                      <div key={group.id} style={{ minWidth: 'max-content' }}>
                        {group.name}
                      </div>
                    ))
                  ) : (
                    <div className="group-row">&#8212;</div>
                  )}
                </>
              );
            },
          },
        ]
      : []),
    {
      field: 'user.companyData.companyName',
      exportName: 'companyName',
      header: t('generic.companyName'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_COMPANY_NAME,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.companyName'),
      },
      render: (row: CampaignUserResult) =>
        row.user?.companyData?.companyName ? (
          row.user?.companyData?.companyName
        ) : (
          <span>&#8212;</span>
        ),
    },
    {
      field: 'user.companyData.department',
      exportName: 'department',
      header: t('generic.department'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_DEPARTMENT,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.department'),
      },
      render: (row: CampaignUserResult) =>
        row.user?.companyData?.department ? (
          row.user?.companyData?.department
        ) : (
          <span>&#8212;</span>
        ),
    },
    {
      field: 'user.companyData.manager',
      exportName: 'manager',
      header: t('generic.manager'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_MANAGER,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.manager'),
      },
      render: (row: CampaignUserResult) =>
        row.user?.companyData?.manager ? (
          row.user?.companyData?.manager
        ) : (
          <span>&#8212;</span>
        ),
    },
    {
      field: 'user.companyData.country',
      exportName: 'country',
      header: t('generic.country'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_COUNTRY,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.country'),
      },
      render: (row: CampaignUserResult) =>
        row.user?.companyData?.country ? (
          row.user?.companyData?.country
        ) : (
          <span>&#8212;</span>
        ),
    },
    {
      field: 'user.companyData.jobTitle',
      exportName: 'jobTitle',
      header: t('generic.jobTitle'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_JOB,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.jobTitle'),
      },
      render: (row: CampaignUserResult) =>
        row.user?.companyData?.jobTitle ? (
          row.user?.companyData?.jobTitle
        ) : (
          <span>&#8212;</span>
        ),
    },
    {
      field: 'user.companyData.mobilePhone',
      exportName: 'mobilePhone',
      header: t('generic.mobilePhone'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_MOBILE_PHONE,
        type: FilterTypeEnum.TEXT,
        placeholder: t('filter.mobilePhone'),
      },
      render: (row: CampaignUserResult) =>
        row.user?.companyData?.mobilePhone ? (
          row.user?.companyData?.mobilePhone
        ) : (
          <span>&#8212;</span>
        ),
    },
    {
      field: 'user.companyData.officeLocation',
      exportName: 'officeLocation',
      header: t('generic.officeLocation'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_OFFICE_LOCATION,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.officeLocation'),
      },
      render: (row: CampaignUserResult) =>
        row.user?.companyData?.officeLocation ? (
          row.user?.companyData?.officeLocation
        ) : (
          <span>&#8212;</span>
        ),
    },
    {
      field: 'status',
      header: t('generic.status'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.CAMPAIGNS_REPORT_BY_STATUS,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('generic.status.search'),
        options: getCampaignUserResultStatuses(
          t,
          canUseFeature(Subjects.EMAIL_OPEN_EVENTS),
        ),
      },
      render: (row: CampaignUserResult) =>
        getCampaignReportsStatusSeverity(row.status, t),
    },
    {
      field: 'lastEventStatus',
      header: t('generic.latestActivity'),
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.CAMPAIGNS_REPORT_BY_LATEST_ACTIVITY,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('generic.status.search'),
        options: getCampaignEventStatuses(
          t,
          canUseFeature(Subjects.EMAIL_OPEN_EVENTS),
          canUseFeature(Subjects.OUTLOOK_PHISHING_EMAIL_REPORTING),
        ),
      },
      render: (row: CampaignUserResult) =>
        getCampaignReportsStatusSeverity(row.lastEventStatus, t, true),
    },
    ...(canUseFeature(Subjects.OUTLOOK_PHISHING_EMAIL_REPORTING)
      ? [
          {
            field: 'meta.reported',
            header: t('campaign.email.reported.short'),
            sortable: true,
            filterable: true,
            filters: {
              field: FilterNamesEnum.EMAIL_REPORTED,
              type: FilterTypeEnum.SELECT_YES_NO,
              placeholder: t('generic.select'),
            },
            render: (row: CampaignUserResult) =>
              getRepotedStatus(row.meta?.reported),
          },
        ]
      : []),

    ...(can(Actions.READ, Subjects.FALSE_POSITIVE_IPS)
      ? [
          {
            field: 'falsePositive',
            header: t('campaign.falsePositives'),
            sortable: true,
            filterable: true,
            filters: {
              field: FilterNamesEnum.FALSE_POSITIVE,
              type: FilterTypeEnum.SELECT_YES_NO,
              placeholder: t('generic.select'),
            },
            render: (row: CampaignUserResult) => (
              <span
                style={{
                  display: 'inline-block',
                  padding: 'var(--xxsmall-padding) var(--small-padding)',
                  borderRadius: 'var(--grand-border-radius)',
                  backgroundColor: 'var(--platinum-main)',
                  color: 'var(--gray-darkest)',
                  fontSize: 'var(--xsmall-font-size)',
                  lineHeight: 'var(--xsmall-line-height)',
                }}
              >
                {hasFalsePositive(row)
                  ? t('campaign.falsePositives.yes')
                  : t('campaign.falsePositives.no')}
              </span>
            ),
          },
        ]
      : []),
    ...(showRunColumns
      ? ([
          {
            field: 'campaignLanguage',
            header: t('generic.lang'),
            sortable: true,
            filterable: false,
            render: (row: MasterTemplateUserResult) =>
              row.campaignLanguage ? (
                getPhishingLanguageLabel(row.campaignLanguage)
              ) : (
                <span>&#8212;</span>
              ),
          },
          {
            field: 'campaignLaunchDate',
            header: t('generic.launchDate'),
            sortable: true,
            filterable: false,
            render: (row: MasterTemplateUserResult) =>
              row.campaignLaunchDate ? (
                <FormatDate
                  date={row.campaignLaunchDate}
                  format={DateFormats.DATEONLY}
                />
              ) : (
                <span>&#8212;</span>
              ),
          },
        ] as DataTableColumnType[])
      : []),
    { field: 'spacer', header: '', sortable: false, filterable: false },
  ];

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const defaultVisibleColumns = columns
    .filter(
      (column) =>
        ![
          'companyName',
          'country',
          'officeLocation',
          'department',
          'manager',
          'jobTitle',
          'mobilePhone',
        ].includes(column.exportName || column.field),
    )
    .map((column) => column.exportName || column.field);
  const alwaysVisibleColumns = ['email', 'spacer'];
  //

  const onFilter = (values: Record<FilterNamesEnum, FilterValue>) => {
    setSkip(0);
    setFilterValues(values);
    const newFilters = getFilters(values);
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
    onRawFiltersChange?.(values);
  };

  const toolbar = (
    <DataTableToolbar>
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        <DataTableFilters
          ref={filtersRef}
          filters={getFiltersFromColumns(columns)}
          onFilter={onFilter}
          defaultValues={defaultTableFilters}
          tableName={TableNamesEnum.CAMPAIGN_REPORTS}
        />
        {toolbarAdditional}
      </FlexContainer>

      {!hideExport && (
        <>
          <FlexContainer gap={12} justify="flex-end" width="auto">
            <AppButton
              icon="pi pi-download"
              label={t('generic.export')}
              onClick={() => setShowExportModal(true)}
            />
          </FlexContainer>

          <ReportsExportModal
            type={UsersReportType.PHISHING}
            visibleColumns={visibleColumns}
            filters={apiFilters}
            visible={showExportModal}
            onHide={() => setShowExportModal(false)}
            campaign={campaign}
          />
        </>
      )}

      <DataTableAPIColumnsMultiSelect
        columns={columns}
        tableName={TableNamesEnum.CAMPAIGN_REPORTS}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        alwaysVisibleColumns={alwaysVisibleColumns}
      />
    </DataTableToolbar>
  );

  const handleRowClick = (e: DataTableRowClickEvent) => {
    const target = e.originalEvent.target as HTMLElement;
    if (target.closest('.p-row-toggler')) return;

    const rowId = (e.data as CampaignUserResult).id;
    setExpandedRows((prev: any) => {
      if (prev?.[rowId]) {
        const next = { ...prev };
        delete next[rowId];
        return Object.keys(next).length ? next : undefined;
      }
      return { ...prev, [rowId]: true };
    });
  };

  const rowExpansionTemplate = (
    data: CampaignUserResult & Partial<MasterTemplateUserResult>,
  ) => (
    <CampaignUserEventsTimeline
      userResults={data}
      status={data.status}
      campaignCreationDate={(data.campaignCreated ?? campaign?.created) as Date}
    />
  );

  return (
    <DataTable
      data={userResults?.result}
      count={userResults?.count}
      isLoading={isLoading}
      isFetching={isFetching}
      toolbar={withToolbar ? toolbar : null}
      columns={columns}
      visibleColumns={visibleColumns}
      rows={take}
      skip={skip}
      onPage={onPage}
      onSort={onSort}
      sort={sort}
      expandedRows={expandedRows}
      onRowToggle={(e: any) => {
        setExpandedRows(e.data);
      }}
      rowExpansionTemplate={rowExpansionTemplate}
      allowExpansion
      onRowClick={handleRowClick}
      stickyScrollbar
    />
  );
};
